// The week's review: what each block became, read from its time and what moved
// in trameria meanwhile (one request a week). What moved in a block opens under
// it, and a move not linked to the block is linked with one press.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { addDays, weekStartOf } from "@trameria/core/cases-shared";
import {
  type BlockActivity,
  type BlockDid,
  blockPlan,
  type ReviewBlock,
  type TimeBlockerReview,
} from "@trameria/core/timeblocker-shared";
import { m } from "@trameria/i18n";
import { useMemo, useState } from "react";
import { useApi } from "../app/context";
import { dateIn, formatMonthDay, todayIn } from "../cases/dates";
import { HEADER_PILL } from "../cases/detail";
import { apiErrorMessage } from "../data/errors";
import { useProfile, useTimeZone } from "../me/profile";
import { useShellFrame } from "../shell/frame";
import { PanelHeader } from "../shell/panel";
import { zonedParts } from "../time/format";
import { ICON_BUTTON } from "../ui/button";
import { Icon, type IconKind } from "../ui/icon";
import { blockLabel, formatDuration } from "./day";
import { reviewPrefix, reviewQuery, useLinkBlock } from "./queries";
import { dayLabel } from "./screen";

export const TIMEBLOCKER_REVIEW_PATH = "/me/timeblocker/review";

const timeOf = (iso: string, timeZone?: string) => {
  const p = zonedParts(new Date(iso), timeZone);
  return `${p.hour}:${String(p.minute).padStart(2, "0")}`;
};
const percent = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–";

const DID: Record<BlockDid, { label: () => string; className: string }> = {
  started: {
    label: () => m.timeblocker_did_started(),
    className: "bg-accent/15 text-accent",
  },
  activity: {
    label: () => m.timeblocker_did_activity(),
    className: "bg-accent/10 text-accent",
  },
  none: {
    label: () => m.timeblocker_did_none(),
    className: "bg-hover text-faint",
  },
  upcoming: {
    label: () => m.timeblocker_did_upcoming(),
    className: "border border-border-soft text-faint",
  },
};
const KIND: Record<
  BlockActivity["kind"],
  { icon: IconKind; label: () => string }
> = {
  step_closed: {
    icon: "check",
    label: () => m.timeblocker_activity_step_closed(),
  },
  case_closed: {
    icon: "cases",
    label: () => m.timeblocker_activity_case_closed(),
  },
  agent_run: {
    icon: "agentChat",
    label: () => m.timeblocker_activity_agent_run(),
  },
};

export function TimeBlockerReviewScreen() {
  const profile = useProfile();
  if (!profile.data) return null;
  return <Review />;
}

function Review() {
  const api = useApi();
  const frame = useShellFrame();
  const timeZone = useTimeZone();
  const thisWeek = weekStartOf(todayIn(timeZone));
  const [week, setWeek] = useState(thisWeek);
  const { data, error } = useQuery(reviewQuery(api, week, timeZone));
  const range = m.timeblocker_week_range({
    from: formatMonthDay(week),
    to: formatMonthDay(addDays(week, 6)),
  });
  return (
    <>
      <PanelHeader
        header={{
          // The app draws only the title: the week goes in it.
          title: m.timeblocker_review_of({ range }),
          back: true,
          up: "/me/timeblocker",
          note: range,
          actions: [
            {
              id: "timeblocker.review.prev",
              label: m.timeblocker_prev_week(),
              icon: "back",
              onPress: () => setWeek(addDays(week, -7)),
            },
            {
              id: "timeblocker.review.next",
              label: m.timeblocker_next_week(),
              icon: "forward",
              onPress: () => setWeek(addDays(week, 7)),
            },
            {
              id: "timeblocker.review.this",
              label: m.timeblocker_this_week(),
              style: "pill",
              disabled: week === thisWeek,
              onPress: () => setWeek(thisWeek),
            },
          ],
        }}
        // A phone draws the plain title (with the week); wider, the way back to the timeline.
        title={
          frame.phone ? undefined : (
            <span className="flex min-w-0 items-center gap-1.5">
              <Link
                to="/me/timeblocker"
                className="text-muted hover:text-fg max-sm:hidden"
              >
                {m.timeblocker_title()}
              </Link>
              <span className="text-faint max-sm:hidden">/</span>
              <span>{m.timeblocker_review()}</span>
            </span>
          )
        }
        actions={
          <span className="flex flex-none items-center gap-[2px]">
            <button
              type="button"
              aria-label={m.timeblocker_prev_week()}
              onClick={() => setWeek(addDays(week, -7))}
              className={ICON_BUTTON}
            >
              <Icon kind="back" size={14} stroke={1.5} />
            </button>
            <span className="min-w-[8em] whitespace-nowrap px-1 text-center text-[13px] tabular-nums text-fg">
              {range}
            </span>
            <button
              type="button"
              aria-label={m.timeblocker_next_week()}
              onClick={() => setWeek(addDays(week, 7))}
              className={ICON_BUTTON}
            >
              <Icon kind="forward" size={14} stroke={1.5} />
            </button>
            <button
              type="button"
              disabled={week === thisWeek}
              onClick={() => setWeek(thisWeek)}
              className={`${HEADER_PILL} ml-1 disabled:cursor-default disabled:opacity-40 disabled:hover:border-pill-border disabled:hover:text-body-soft`}
            >
              {m.timeblocker_this_week()}
            </button>
          </span>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-[860px] flex-col gap-6 px-4 pt-4 pb-10">
          {error && (
            <p role="alert" className="text-[12.5px] text-danger">
              {apiErrorMessage(error, m.timeblocker_save_failed())}
            </p>
          )}
          {data && <Totals review={data} />}
          {data && <Days review={data} week={week} timeZone={timeZone} />}
        </div>
      </div>
    </>
  );
}

function Totals({ review: r }: { review: TimeBlockerReview }) {
  const tiles = [
    {
      label: m.timeblocker_review_done(),
      value: String(r.doneCount),
      note: m.timeblocker_review_done_of(),
    },
    {
      label: m.timeblocker_review_planned(),
      value: formatDuration(r.plannedMs),
      note: m.timeblocker_review_planned_committed(),
    },
    {
      label: m.timeblocker_review_actual(),
      value: formatDuration(r.actualMs),
      note: null,
    },
    {
      label: m.timeblocker_review_rate(),
      value: percent(r.didBlocks, r.blocks),
      note: m.timeblocker_review_rate_of({
        did: String(r.didBlocks),
        all: String(r.blocks),
      }),
    },
    {
      label: m.timeblocker_review_ratio(),
      value: percent(r.overActualMs, r.overPlannedMs),
      note: m.timeblocker_review_ratio_of({
        planned: formatDuration(r.overPlannedMs),
      }),
    },
  ];
  // Only the kinds that happened: none at all and the line is not there.
  const changes = (
    [
      [r.movedBlocks, m.timeblocker_review_changes_moved],
      [r.shortenedBlocks, m.timeblocker_review_changes_shortened],
      [r.deferredBlocks, m.timeblocker_review_changes_deferred],
    ] as const
  ).flatMap(([n, text]) => (n ? [String(text({ count: String(n) }))] : []));
  return (
    <div className="flex flex-col gap-2">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {tiles.map((t, i) => (
          <div
            key={t.label}
            className={`flex flex-col gap-0.5 rounded-[8px] border border-border-soft px-3 py-2.5 ${i === 0 ? "max-sm:col-span-2" : ""}`}
          >
            <dt className="text-[12px] text-muted">{t.label}</dt>
            <dd className="text-[22px] leading-[28px] tabular-nums text-fg">
              {t.value}
            </dd>
            {t.note && (
              <dd className="truncate text-[11px] text-faint">{t.note}</dd>
            )}
          </div>
        ))}
      </dl>
      {changes.length > 0 && (
        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-1 text-[12px] text-muted">
          <span className="text-faint">{m.timeblocker_review_changes()}</span>
          {changes.map((c) => (
            <span key={c} className="tabular-nums">
              {c}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/** Where a block sits in the review: its committed plan, else (moved in from before) where it is now. */
const reviewAt = (item: ReviewBlock) =>
  item.planned ? blockPlan(item.block) : item.block;

function Days({
  review,
  week,
  timeZone,
}: {
  review: TimeBlockerReview;
  week: string;
  timeZone?: string;
}) {
  const days = useMemo(() => {
    const by = new Map<string, ReviewBlock[]>();
    for (const item of review.items) {
      const day = dateIn(reviewAt(item).startAt, timeZone);
      // A block begun in the week before is shown on its first day here.
      const at = day < week ? week : day;
      by.set(at, [...(by.get(at) ?? []), item]);
    }
    return [...by.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [review, week, timeZone]);
  if (!days.length)
    return (
      <p className="py-6 text-center text-[13px] text-faint">
        {m.timeblocker_review_empty()}
      </p>
    );
  return (
    <div className="flex flex-col gap-5">
      {days.map(([day, items]) => (
        <section key={day} aria-label={dayLabel(day)}>
          <h2 className="mb-1.5 flex items-baseline gap-2 text-[12.5px] text-muted">
            <span>{dayLabel(day)}</span>
            <span className="tabular-nums text-faint">
              {/* A day all to come has no actual time yet: its plan alone. */}
              {items.some((i) => i.did !== "upcoming") &&
                `${formatDuration(items.reduce((n, i) => n + i.actualMs, 0))} / `}
              {formatDuration(
                items.reduce((n, i) => n + (i.planned ? i.plannedMs : 0), 0),
              )}
            </span>
          </h2>
          <ul className="flex flex-col divide-y divide-border-soft rounded-[8px] border border-border-soft">
            {items.map((item) => (
              <BlockRow
                key={item.block.id}
                item={item}
                day={day}
                timeZone={timeZone}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function BlockRow({
  item,
  day,
  timeZone,
}: {
  item: ReviewBlock;
  day: string;
  timeZone?: string;
}) {
  const [open, setOpen] = useState(false);
  const { block, did, activity } = item;
  const label = blockLabel(block);
  const span = (b: { startAt: string; endAt: string }) =>
    `${timeOf(b.startAt, timeZone)}–${timeOf(b.endAt, timeZone)}`;
  const time = span(reviewAt(item));
  const committedPlan = blockPlan(block);
  // How it was changed after its start came, against the plan shown.
  const change = item.change
    ? item.planned
      ? item.change.deferred
        ? m.timeblocker_review_deferred_to({
            day: formatMonthDay(dateIn(block.startAt, timeZone)),
            time: span(block),
          })
        : m.timeblocker_review_moved_to({ time: span(block) })
      : m.timeblocker_review_deferred_from({
          day: formatMonthDay(dateIn(committedPlan.startAt, timeZone)),
          time: span(committedPlan),
        })
    : null;
  // The Cases it is linked to, but the one already its name.
  const caseTitles = [...new Set(block.links.map((l) => l.caseTitle))].filter(
    (t) => t !== label,
  );
  return (
    <li className="px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="w-[6.5em] flex-none text-[12px] tabular-nums text-muted max-sm:hidden">
          {time}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] leading-[20px] text-fg">
            {label}
          </div>
          {/* A phone has no room for the time column: it leads this line. */}
          {(caseTitles.length > 0 || !block.links.length) && (
            <div className="truncate text-[11.5px] leading-[16px] text-faint">
              <span className="tabular-nums text-muted sm:hidden">
                {time}
                {" · "}
              </span>
              {block.links.length
                ? caseTitles.join(" · ")
                : m.timeblocker_review_unlinked_block()}
            </div>
          )}
          {caseTitles.length === 0 && block.links.length > 0 && (
            <div className="truncate text-[11.5px] leading-[16px] tabular-nums text-muted sm:hidden">
              {time}
            </div>
          )}
          {change && (
            <div className="flex min-w-0 items-center gap-1.5 text-[11.5px] leading-[16px] text-muted">
              <span className="flex-none rounded-[4px] border border-border-soft px-1 text-[10.5px] leading-[14px] text-faint">
                {item.change?.deferred || !item.planned
                  ? m.timeblocker_change_deferred()
                  : m.timeblocker_change_moved()}
              </span>
              <span className="truncate tabular-nums">{change}</span>
            </div>
          )}
        </div>
        {item.done > 0 && (
          <span className="flex flex-none items-center gap-0.5 text-[12px] tabular-nums text-muted">
            <Icon kind="check" size={12} />
            {item.done}
          </span>
        )}
        <span className="w-[3.2em] flex-none text-right text-[12px] tabular-nums text-muted">
          {did === "upcoming" ? "" : formatDuration(item.actualMs)}
        </span>
        <span
          className={`flex-none rounded-full px-2 py-[1px] text-[11px] ${DID[did].className}`}
        >
          {DID[did].label()}
        </span>
      </div>
      {activity.length > 0 && (
        <div className="mt-1 pl-[calc(6.5em+12px)] max-sm:pl-0">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex cursor-pointer items-center gap-1 rounded-[6px] py-0.5 text-[12px] text-muted hover:text-fg"
          >
            <span
              className={`flex transition-transform duration-200 ${open ? "rotate-90" : ""}`}
            >
              <Icon kind="forward" size={11} stroke={1.5} />
            </span>
            {m.timeblocker_review_moved({ count: String(activity.length) })}
          </button>
          <div
            className={`grid transition-[grid-template-rows,opacity] duration-200 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
          >
            <ul
              inert={!open}
              className="flex min-h-0 flex-col gap-0.5 overflow-hidden"
            >
              {activity.map((a) => (
                <ActivityRow
                  key={`${a.kind}:${a.chatId ?? a.stepId ?? a.caseId}:${a.at}`}
                  activity={a}
                  blockId={block.id}
                  day={day}
                  timeZone={timeZone}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}

function ActivityRow({
  activity: a,
  blockId,
  day,
  timeZone,
}: {
  activity: BlockActivity;
  blockId: string;
  day: string;
  timeZone?: string;
}) {
  const link = useLinkBlock({ untracked: true });
  const qc = useQueryClient();
  const kind = KIND[a.kind];
  const linkIt = () => {
    if (!a.caseId) return;
    const target = { caseId: a.caseId, stepId: a.stepId };
    // Shown linked at once; the review is read again on the block's change.
    const mark = (linked: boolean) =>
      qc.setQueriesData<TimeBlockerReview>({ queryKey: reviewPrefix }, (old) =>
        old
          ? {
              ...old,
              items: old.items.map((i) =>
                i.block.id !== blockId
                  ? i
                  : {
                      ...i,
                      activity: i.activity.map((x) =>
                        x.caseId === a.caseId &&
                        (x.stepId === a.stepId || !a.stepId)
                          ? { ...x, linked }
                          : x,
                      ),
                    },
              ),
            }
          : old,
      );
    mark(true);
    link.mutate(
      { day, timeZone, blockId, target },
      { onError: () => mark(false) },
    );
  };
  return (
    <li className="flex min-w-0 items-center gap-2 py-0.5 text-[12.5px]">
      <span className="flex-none text-faint" title={kind.label()}>
        <Icon kind={kind.icon} size={12} />
      </span>
      <span className="sr-only">{kind.label()}</span>
      <span className="w-[3em] flex-none tabular-nums text-faint">
        {timeOf(a.at, timeZone)}
      </span>
      <span className="min-w-0 flex-1 truncate text-body-soft">
        {a.title ||
          (a.kind === "agent_run" ? m.timeblocker_review_untitled_chat() : "")}
        {a.caseTitle && (
          <span className="ml-1.5 text-faint">{a.caseTitle}</span>
        )}
      </span>
      {a.linked ? (
        <span className="flex-none text-[11.5px] text-faint">
          {m.timeblocker_review_linked()}
        </span>
      ) : a.caseId ? (
        <button
          type="button"
          onClick={linkIt}
          className={`${HEADER_PILL} flex-none`}
        >
          <Icon kind="link" size={11} />
          {m.timeblocker_review_link()}
        </button>
      ) : null}
    </li>
  );
}
